import React, { useState, Fragment, useRef, useEffect } from "react";
import { externalUrl } from "../../../data/externalUrls";
import CsDiagnostics from "../../../models/CsDiagnostics";
import { toast } from "react-toastify";
import axios from "axios";

const EventSummary = (props) => {
  const { csDiagnosticsParams, setIsLoading } = props;
  const [eventSummaryData, setEventSummaryData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const cellRefs = useRef([]);
  const [searchValues, setSearchValues] = useState({});
  const [searchValue, setSearchValue] = useState("");
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);

  useEffect(() => {
    setEventSummaryData([]);
  }, [csDiagnosticsParams])

  const beautifyText = (text) => {
    return text.split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
  };

  let filteredData = eventSummaryData?.filter((row) =>
    headers.every((key) =>
      row[key] !== null ? row[key]?.toString().toLowerCase().includes(searchValues[key]?.toLowerCase() || "") : 'null'
    )
  );

  const handleSearchChange = (e, key) => {
    setSearchValues({
      ...searchValues,
      [key]: e.target.value.toLowerCase(),
    });
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "Enter" && searchValue.trim() !== "") {
      const matches = [];
      const value = searchValue.toLowerCase();

      // Highlight all matches
      filteredData.forEach((row, rowIndex) => {
        headers.forEach((key, colIndex) => {
          const cellValue = row[key]?.toString() || "";

          // Find matches and wrap in span
          const targetCell = cellRefs.current[`${rowIndex}-${colIndex}`];
          if (targetCell) {
            const highlightedHtml = cellValue.replace(
              new RegExp(value, "gi"),
              (match) => `<span class="bg-warning">${match}</span>`
            );
            targetCell.innerHTML = highlightedHtml; // Update cell with highlighted text

            // Track all matches for navigation
            const cellMatches = [...targetCell.querySelectorAll("span.bg-warning")];
            cellMatches.forEach((match) => matches.push(match));
          }
        });
      });

      if (matches.length > 0) {
        // Navigate to the next match
        const newIndex = (currentMatchIndex + 1) % matches.length;
        setCurrentMatchIndex(newIndex);

        matches.forEach((match, index) => {
          match.classList.toggle("bg-danger", index === newIndex);
        });

        const currentMatch = matches[newIndex];
        currentMatch.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  };

  const fetchData = async(params) => {
    try {
      setIsLoading(true);
      const formData = new URLSearchParams();
      formData.append('device_id', params.cs_diagonostics.device_id);
      formData.append('start_time', params.cs_diagonostics.start_date);
      formData.append('end_time', params.cs_diagonostics.end_date);

      await axios.post(`${externalUrl.flaskServiceUrl}/CS_Diagnostics/event_summary`, formData)
      .then((response) => {
        if (response?.data?.data && response?.data?.data.length > 0) {
          setEventSummaryData(response?.data?.data);
          setHeaders(Array.from(new Set(response?.data?.data.flatMap(obj => Object.keys(obj)))));
        } else {
          toast.warning("Data not found or empty.")
        }
      })
      .catch(error => {
        toast.error(error?.message);
      });
    } catch(error) {
      toast.error(error?.data?.message || 'Something went wrong !');
    } finally {
      setIsLoading(false);
    }
  }

  const onEventSummaryDataLoadClick = () => {
    try {
      fetchData((new CsDiagnostics(csDiagnosticsParams)).toApiJson());
    } catch(error) {
      setEventSummaryData([]);
      toast.error(error);
      // setCsDiagnostics(new CsDiagnostics(csDiagnosticsParams));
    }
  }

  return <Fragment>
    <div
      className="border border-success border-1 rounded-2 border-opacity-25 p-2"
      style={{ height: "440px", overflow: "hidden", backgroundColor: "#ecf2ea" }}
    >
      <div className="d-flex">
        <div className="w-100">
          <h6 className="text-success text-center">EVENT SUMMARY</h6>
        </div>
        <div className="flex-shrink-1 m-1">
          <input
            id="searchValue"
            placeholder={`Search`}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={onSearchKeyDown}
            value={searchValue}
            className="s-round-input"
            type="text"
            style={{
              padding: "10px 50px 10px 15px",
              height: "30px",
              width: "140px",
              fontSize: "12px",
            }}
          />
        </div>
        <div className="flex-shrink-1">
          <button type="button" className="btn btn-sm btn-voltup-theme" onClick={onEventSummaryDataLoadClick}>
            Load
          </button>
        </div>
      </div>
      <div
        className="table-responsive custom-scrollbar border"
        style={{ height: "90%", overflowY: "auto" }}
      >
        <table className="table align-top table-sm table-borderless">
          <thead className="table-light" style={{ position: "sticky", top: "0" }}>
            <tr>
              {headers.map((key) => (
                <th key={key} scope="col" style={{ minWidth: "100px" }}>
                  {beautifyText(key)}
                </th>
              ))}
            </tr>
            <tr>
              {headers.map((key) => (
                <th key={key}>
                  <input
                    type="text"
                    className="form-control form-control-sm font-size-12"
                    placeholder={`Search ${key}`}
                    onChange={(e) => handleSearchChange(e, key)}
                    value={searchValues[key] || ""}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredData?.length > 0 ? (
              filteredData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {headers.map((key, colIndex) => (
                    <td
                      key={key}
                      className="border"
                      ref={(el) => (cellRefs.current[`${rowIndex}-${colIndex}`] = el)}
                      style={{
                        maxWidth: '350px',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        // wordBreak: 'break-word'
                      }}
                    >
                      {row[key] || '- -'}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={headers.length} className="text-center">
                  No matching records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  </Fragment>
}

export default EventSummary;
